import type { Tester, ConditionConfigRow } from "../types";
import type { DayInfo } from "../lib/days";
import { parseBroadcastNote } from "../lib/broadcast";
import { useVerifications } from "../hooks/useVerifications";
import { SetupTasksSection } from "../components/conditions/SetupTasksSection";
import { DailyProfileChecksSection } from "../components/conditions/DailyProfileChecksSection";
import { WorkoutSyncSection } from "../components/conditions/WorkoutSyncSection";
import { ChallengeChecksSection } from "../components/conditions/ChallengeChecksSection";
import { SharingTestsSection } from "../components/conditions/SharingTestsSection";

interface DashboardPageProps {
  profile: Tester;
  dayInfo: DayInfo;
  cohortStart: Date;
  today: Date;
  broadcastNote: string;
  asanaUrl: string;
  conditionConfig: ConditionConfigRow[];
}

export function DashboardPage({
  profile,
  dayInfo,
  cohortStart,
  today,
  broadcastNote,
  asanaUrl,
  conditionConfig,
}: DashboardPageProps) {
  const { loading, getStatus, setStatus } = useVerifications(profile.email);

  const dailyProfileChecks = conditionConfig.filter(
    (c) => c.section === "daily_profile",
  );

  const fullName = `${profile.first_name} ${profile.last_name}`;
  const wearable = profile.wearable_platform || "—";
  const activity = profile.secondary_activity
    ? `${profile.primary_activity} · ${profile.secondary_activity}`
    : profile.primary_activity;

  return (
    <div className="px-4 py-4 max-w-md mx-auto pb-24">
      {broadcastNote && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 border-l-4 border-l-amber-500 p-3">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 mb-1">
            Broadcast
          </div>
          <div className="text-xs text-amber-900 leading-relaxed space-y-1.5">
            {parseBroadcastNote(broadcastNote).map((block, i) =>
              block.kind === "list" ? (
                <ul key={i} className="list-disc pl-4 space-y-0.5">
                  {block.items.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              ) : (
                // pre-line keeps single newlines the author typed, without
                // needing <br> injection.
                <p key={i} className="whitespace-pre-line">
                  {block.text}
                </p>
              ),
            )}
          </div>
        </div>
      )}

      <div className="rounded-lg bg-white border border-zinc-200 p-4 mb-4">
        <div className="text-base font-semibold text-zinc-900">{fullName}</div>
        <div className="text-xs text-zinc-500 mt-0.5">
          {profile.tester_type} · {wearable} · {profile.device_type}
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-3 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">Primary</div>
            <div className="text-zinc-800">{activity}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">Email</div>
            <div className="text-zinc-800 truncate" title={profile.email}>
              {profile.email}
            </div>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-dashed border-zinc-200 text-sm font-semibold text-rwb-red">
          {dayInfo.dayLabel}
        </div>

        {profile.challenges.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1.5">
              Assigned challenges
            </div>
            <div className="flex flex-wrap gap-1.5">
              {profile.challenges.map((c) => (
                <span
                  key={c}
                  className="inline-block bg-rose-50 text-rose-900 text-[10px] px-2 py-0.5 rounded-full"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="rounded-lg bg-white border border-dashed border-zinc-300 p-5 text-center text-xs text-zinc-500">
          Loading your checklist...
        </div>
      ) : (
        <>
          <SetupTasksSection
            profile={profile}
            getStatus={getStatus}
            setStatus={setStatus}
          />

          <DailyProfileChecksSection
            profile={profile}
            currentDayNum={dayInfo.currentDay}
            cohortStart={cohortStart}
            asanaUrl={asanaUrl}
            dailyProfileChecks={dailyProfileChecks}
            getStatus={getStatus}
            setStatus={setStatus}
          />

          <WorkoutSyncSection
            profile={profile}
            currentDayNum={dayInfo.currentDay}
            windowDays={dayInfo.windowDays}
            cohortStart={cohortStart}
            asanaUrl={asanaUrl}
            getStatus={getStatus}
            setStatus={setStatus}
          />

          <ChallengeChecksSection
            profile={profile}
            currentDayNum={dayInfo.currentDay}
            cohortStart={cohortStart}
            asanaUrl={asanaUrl}
            getStatus={getStatus}
            setStatus={setStatus}
          />

          <SharingTestsSection
            profile={profile}
            currentDayNum={dayInfo.currentDay}
            today={today}
            asanaUrl={asanaUrl}
            getStatus={getStatus}
            setStatus={setStatus}
          />
        </>
      )}
    </div>
  );
}
